import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Translate } from 'react-localize-redux';
import { useFormikContext } from 'formik-2';
import WizardLayout from './WizardLayout';
import StepButtons from './StepButtons';
import css from './TestChannels.css';
import {
    TEST_CHANNELS_LIMIT,
    EMPTY_TEST_CHANNEL,
    DISTRIBUTION_CHANNEL_INIT_VALUE,
    WIZARD_TEST_CHANNELS,
} from '../consts';
import Tooltip from '../../../../components/molecules/Tooltip';
import CollapsableNetwork from './components/CollapsableNetwork';
import useWizard from '../hook/useWizard';
import { sanitize } from '../../../../components/utilities/Sanitize';
import { route } from '../../../../utils/routerUtils';
import GeneralPopup, { PopupTypes } from '../../../../components/organisms/GeneralPopup';

const translatePrefix = 'STATIC.PAGES.AUDIENCES.AUDIENCE_AB_TESTING.WIZARD.SECTION_COMPONENTS.TEST_CHANNELS';
export const WIZARD_DATA_TEST_ID_TEST_CHANNELS = 'test-channels-wizard';

const getDistributionId = (distributionData, testChannelName) =>
    distributionData?.[testChannelName]?.distribution?.id ?? null;

const getNetworkId = (distributionData, networkName) => distributionData?.[networkName]?.id ?? null;

const getConfig = (distributionData, networkName) => {
    const config = {};
    const segmentFields = distributionData?.[networkName]?.segment_fields;
    if (Array.isArray(segmentFields)) {
        segmentFields.forEach(field => {
            field.required && (config[field.name] = '');
        });
    }
    return config;
};

const TestChannels = ({ onSaveDraft, pageTitle, onBack, onNext, submitData, distributionsData }) => {
    const {
        setFieldValue,
        values,
        setErrors,
        errors,
        setFieldTouched,
        touched,
        dirty: isFormDirty,
    } = useFormikContext();
    const { shouldShowDraft, isAlreadyStarted, isDuplicateNetworkSelected } = useWizard(values);
    const [showPopupRedirect, setPopupRedirect] = useState(false);
    const [expanded, setExpanded] = useState(Array(values.testChannels.length).fill(values.testChannels.length === 1));
    const testChannelErrors = errors?.testChannels?.length ? errors.testChannels : [];
    const testChannelTouched = touched?.testChannels?.length ? touched.testChannels : [];

    const onNetworkChange = async (network, rowIndex) => {
        const value =
            values.testChannels.length > 1 ? DISTRIBUTION_CHANNEL_INIT_VALUE : DISTRIBUTION_CHANNEL_INIT_VALUE * 2;
        setExpanded([...expanded, true]);
        setFieldValue(`testChannels.${rowIndex}`, {
            name: network.name,
            config: getConfig(distributionsData.distributionNetworks, network.name),
            uanId: getDistributionId(distributionsData.distributionNetworks, network.name),
            adnetworkIntegrationId: getNetworkId(distributionsData.distributionNetworks, network.name),
            segmentFields: distributionsData.distributionNetworks?.[network.name]?.segment_fields || [],
            value,
        });
    };

    const onDistributionFieldChange = async (rowIndex, fieldName, value) => {
        const path = `testChannels.${rowIndex}.config.${fieldName}`;
        setFieldTouched(path, true);
        setFieldValue(path, value);
    };

    const onAdd = () => {
        if (TEST_CHANNELS_LIMIT === values.testChannels.length) return;
        setExpanded([...expanded, true]);
        setFieldValue(`testChannels.${values.testChannels.length - 1}.value`, DISTRIBUTION_CHANNEL_INIT_VALUE);
        setFieldValue(`testChannels.${values.testChannels.length}`, {
            ...EMPTY_TEST_CHANNEL,
            value: DISTRIBUTION_CHANNEL_INIT_VALUE,
        });
    };

    const onDistributionRedirectConfirm = async () => {
        if (values.testChannels.length === 1 && values.testChannels[0].name === '') {
            await setFieldValue(`testChannels`, []);
            setErrors([]);
        }
        await onSaveDraft(WIZARD_TEST_CHANNELS);
        route('react/audiences/distributions');
    };

    const onDistributionPageClick = async () => {
        if (isAlreadyStarted || !isFormDirty) {
            route('react/audiences/distributions');
        } else {
            setPopupRedirect(true);
        }
    };

    const onDelete = index => {
        if (values.testChannels.length <= 1) return;
        const newArray = values.testChannels.filter((_, i) => index !== i);
        newArray[0] = {
            ...newArray[0],
            value: 45,
        };
        setFieldValue(`testChannels`, newArray);
    };

    const onCollapseToggle = index => {
        setExpanded(prev => {
            const newExpanded = [...prev];
            newExpanded[index] = !prev[index];
            return newExpanded;
        });
    };

    return (
        <WizardLayout
            activeKey={WIZARD_TEST_CHANNELS}
            title={pageTitle}
            isContentScrollable={false}
            onSaveDraft={onSaveDraft}
            submitData={submitData}
            shouldShowDraft={shouldShowDraft}
            buttons={
                <StepButtons onBack={onBack} onNext={onNext} backTitle="BUTTONS.BACK" nextTitle="STATIC.BUTTONS.NEXT" />
            }
        >
            <div data-testid={WIZARD_DATA_TEST_ID_TEST_CHANNELS}>
                <Translate
                    data={{
                        testName: sanitize(values.testName),
                    }}
                    id={`${translatePrefix}.SUBTITLE`}
                />
                <span>
                    <span className={css.link} onClick={onDistributionPageClick}>
                        <Translate id={`${translatePrefix}.DISTRIBUTION`} />
                    </span>
                    .
                </span>
                {values.testChannels.map((channel, rowIndex) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <div key={channel.value + rowIndex} className={css.container}>
                        <CollapsableNetwork
                            isDuplicateNetworkSelected={isDuplicateNetworkSelected}
                            allNetworks={distributionsData?.allNetworks || []}
                            // eslint-disable-next-line react/no-array-index-key
                            key={rowIndex + channel.name}
                            testChannel={channel}
                            expanded={expanded[rowIndex]}
                            disabled={isAlreadyStarted}
                            distributionNetworks={distributionsData?.distributionNetworks || {}}
                            onDistributionFieldChange={(field, val) => onDistributionFieldChange(rowIndex, field, val)}
                            onNetworkChange={network => onNetworkChange(network, rowIndex)}
                            onCollapseToggle={() => onCollapseToggle(rowIndex)}
                            onDelete={() => onDelete(rowIndex)}
                            showDelete={values.testChannels.length > 1 || rowIndex > 0}
                            rowIndex={rowIndex}
                            touched={testChannelTouched[rowIndex]}
                            errors={testChannelErrors[rowIndex]}
                        />
                    </div>
                ))}
                {!isAlreadyStarted && (
                    <div
                        onClick={onAdd}
                        className={`${css.addNetworkButton} ${
                            values.testChannels.length >= TEST_CHANNELS_LIMIT ? css.disabled : ''
                        }`}
                    >
                        <Tooltip
                            titleTranslationKey={`${translatePrefix}.ADD_NETWORK_DISABLE_TOOLTIP`}
                            disabled={values.testChannels.length < TEST_CHANNELS_LIMIT}
                        >
                            <Translate id={`${translatePrefix}.ADD_NETWORK`} />
                        </Tooltip>
                    </div>
                )}
                <GeneralPopup
                    open={showPopupRedirect}
                    type={PopupTypes.INFO_WITH_CANCEL}
                    text={`${translatePrefix}.DISTRIBUTION_REDIRECT_TITLE`}
                    rejectText="STATIC.PAGES.AUDIENCES.AUDIENCE_AB_TESTING.WIZARD.CONTINUE_EDIT"
                    title="STATIC.PAGES.AUDIENCES.AUDIENCE_AB_TESTING.WIZARD.SAVE_DRAFT_TITLE"
                    acceptText="STATIC.BUTTONS.SAVE"
                    onAccept={() => onDistributionRedirectConfirm()}
                    onReject={() => setPopupRedirect(false)}
                />
            </div>
        </WizardLayout>
    );
};

TestChannels.propTypes = {
    onNext: PropTypes.func,
    onBack: PropTypes.func,
    onSaveDraft: PropTypes.func,
    submitData: PropTypes.shape({
        loading: PropTypes.bool,
        error: PropTypes.bool,
    }),
    pageTitle: PropTypes.string,
    distributionsData: PropTypes.shape({
        allNetworks: PropTypes.arrayOf(
            PropTypes.shape({
                label: PropTypes.string,
                name: PropTypes.string,
                isDisabled: PropTypes.bool,
            })
        ),
        internalDistributions: PropTypes.any,
        distributionNetworks: PropTypes.any,
    }),
};

export default TestChannels;
